/**
 * Starters from the matter's state (`GET /api/agents/<kind>/<id>/starters`):
 * what is pressing on this client or case comes first, the spec's static
 * starters fill in. Until the call returns, and whenever it fails, the panel
 * shows the static list, so an empty state never waits on the network.
 */
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { queryClient } from "@/lib/query/queryClient";

import { fetchStarters } from "./api/agentApi";
import type { AgentScope, AgentStarter } from "./types";

export const MATTER_STARTERS_STALE_MS = 2 * 60 * 1000;

export const matterStartersKey = (scope: AgentScope) =>
  ["agents", "starters", scope.kind, scope.id] as const;

/** Refetch after a write the starters depend on (a reminder closed, an intake reviewed). */
export const invalidateMatterStarters = (scope?: AgentScope): void => {
  void queryClient.invalidateQueries({
    queryKey: scope ? matterStartersKey(scope) : ["agents", "starters"]
  });
};

export const useMatterStarters = (
  scope: AgentScope,
  fallback: readonly string[]
): { starters: string[]; reasons: Record<string, string> } => {
  // The practice scope has no matter state; its starters are the spec's.
  const enabled = scope.kind === "case" || scope.kind === "client";
  const query = useQuery(
    {
      queryKey: matterStartersKey(scope),
      queryFn: () => fetchStarters(scope),
      staleTime: MATTER_STARTERS_STALE_MS,
      enabled,
      retry: false
    },
    queryClient
  );
  const fetched: AgentStarter[] | undefined = query.data;
  return useMemo(() => {
    if (!enabled || !fetched?.length)
      return { starters: [...fallback], reasons: {} };
    return {
      starters: fetched.map((s) => s.text),
      reasons: Object.fromEntries(fetched.map((s) => [s.text, s.reason]))
    };
  }, [enabled, fetched, fallback]);
};
